import { SignalObject, signalObject } from "omi";
import type { Editor } from "@tiptap/core";

import { HighlightColors } from "@/components/nw-highlight/models";
import { storageSignal } from "@/utils/signals/storageSignal";

type Slice = {
  preferredHighlightColor: HighlightColors;
  activeToolbar: boolean;

  activeHighlightGroup: string | undefined;
  activeInstanceId: string | undefined;
  instances: Map<string, Editor>;

  /**
   * A boolean flag that determines whether editor updates should be saved. This affects all instances
   * @default true
   */
  shouldSaveUpdates: boolean;
};

type Actions = {
  setPreferredHighlightColor: (color: HighlightColors) => void;
  setActiveHighlightGroup: (group: string | undefined) => void;
  setActiveInstanceId: (id: string | undefined) => void;
  registerEditorInstance: (id: string, editor: Editor) => void;
  unregisterEditorInstance: (id: string) => void;
  getActiveEditor: (id?: string) => Editor | undefined;
  isToolbarActive: () => boolean;
  setToolbarActive: (isActive: boolean) => void;
  setShouldSaveUpdates: (shouldSave: boolean) => void;
};

export type EditorSlice = SignalObject<Slice> & Actions;

export function createEditorSlice(): EditorSlice {
  const preferredHighlightColor = storageSignal("editor.preferredHighlightColor", {
    default: HighlightColors.YELLOW,
    storageType: "local",
  });

  const shouldSaveUpdates = storageSignal("editor-save", { storageType: "local", default: true });

  const initialState = {
    activeToolbar: false,
    activeHighlightGroup: undefined,
    activeInstanceId: undefined,
    instances: new Map(),
  } as Slice;

  const state = signalObject(initialState);
  state.preferredHighlightColor = preferredHighlightColor;
  state.shouldSaveUpdates = shouldSaveUpdates;

  return {
    ...state,
    isToolbarActive: isToolbarActiveSelector(state),
    setToolbarActive: setToolbarActiveSelector(state),
    setPreferredHighlightColor: setPreferredHighlightColorSelector(state),
    setActiveHighlightGroup: setActiveHighlightGroupSelector(state),
    setActiveInstanceId: setActiveInstanceIdSelector(state),
    registerEditorInstance: registerEditorInstanceSelector(state),
    unregisterEditorInstance: unregisterEditorInstanceSelector(state),
    getActiveEditor: getActiveEditorSelector(state),
    setShouldSaveUpdates: setShouldSaveUpdatesFunc(state),
  };
}

function setShouldSaveUpdatesFunc(store: SignalObject<Slice>): (shouldSave: boolean) => void {
  return (shouldSave: boolean) => {
    store.shouldSaveUpdates.value = shouldSave;
  };
}

function isToolbarActiveSelector(store: SignalObject<Slice>): () => boolean {
  return () => store.activeToolbar.value;
}

function setToolbarActiveSelector(store: SignalObject<Slice>): (isActive: boolean) => void {
  return (isActive: boolean) => {
    store.activeToolbar.value = isActive;
  };
}

function setPreferredHighlightColorSelector(store: SignalObject<Slice>): (color: HighlightColors) => void {
  return (color: HighlightColors) => {
    store.preferredHighlightColor.value = color;
  };
}

function setActiveHighlightGroupSelector(store: SignalObject<Slice>): (group: string | undefined) => void {
  return (group: string | undefined) => {
    store.activeHighlightGroup.value = group;
  };
}

function setActiveInstanceIdSelector(store: SignalObject<Slice>): (id: string | undefined) => void {
  return (id: string | undefined) => {
    store.activeInstanceId.value = id;
  };
}

function registerEditorInstanceSelector(store: SignalObject<Slice>): (id: string, editor: Editor) => void {
  return (id: string, editor: Editor) => store.instances.value.set(id, editor);
}

function unregisterEditorInstanceSelector(store: SignalObject<Slice>): (id: string) => void {
  return (id: string) => store.instances.value.delete(id);
}

export function getActiveEditorSelector(store: SignalObject<Slice>): () => Editor | undefined {
  return (id?: string) => {
    const _id = id ?? store.activeInstanceId.value;
    return _id ? store.instances.value.get(_id) : undefined;
  };
}
